/**
 * Dashboard → extension project-context handoff.
 *
 * When the user demonstrably starts working in a project on the dashboard
 * (today: creating one), post it to the OverlayQA extension so the sidebar's
 * selected project follows. Without this, the extension keeps whatever was
 * selected last — a real, valid, WRONG project that passes every guard — and
 * workflow bulk creates misfile silently (2026-08-02: 34 issues into the
 * previous day's project while the user worked in the one he just created).
 *
 * The message is same-origin postMessage; the extension content script only
 * accepts it on exact-match overlayqa hosts (trustedHost.ts) and the
 * background only adopts it when the payload's teamId matches the extension's
 * stored team. Posting when the extension is absent is a harmless no-op.
 *
 * The team-review page posts the same message from its own surface
 * (packages/server/src/review-app/lib/projectContextHandoff.ts) — keep the
 * payload shapes in step.
 */
import { log } from '@/lib/logger';

interface HandoffProject {
  id: string;
  name: string;
  teamId: string;
  slug?: string;
  createdAt?: string;
  updatedAt?: string;
}

function parseProject(project: unknown): HandoffProject | null {
  if (!project || typeof project !== 'object') return null;
  const { id, name, teamId, slug, createdAt, updatedAt } = project as Record<string, unknown>;
  if (typeof id !== 'string' || id.length === 0) return null;
  if (typeof name !== 'string' || name.length === 0) return null;
  if (typeof teamId !== 'string' || teamId.length === 0) return null;
  return {
    id,
    name,
    teamId,
    slug: typeof slug === 'string' ? slug : undefined,
    createdAt: typeof createdAt === 'string' ? createdAt : undefined,
    updatedAt: typeof updatedAt === 'string' ? updatedAt : undefined,
  };
}

/**
 * Post the project to the extension. Returns whether a handoff was posted —
 * an unusable project (missing id/name/teamId) is skipped entirely, since a
 * partial handoff could adopt a selection the sidebar cannot render.
 */
export function postExtensionProjectContext(project: unknown, source: string): boolean {
  const parsed = parseProject(project);
  if (!parsed) {
    log.warn('[extensionProjectContext] Skipping handoff — unusable project payload', { source });
    return false;
  }
  try {
    window.postMessage(
      {
        type: 'OVERLAYQA_PROJECT_CONTEXT',
        payload: { project: parsed, source },
      },
      window.location.origin,
    );
    return true;
  } catch (err) {
    // The handoff is an enhancement — it must never break the create flow.
    log.warn('[extensionProjectContext] Failed to post project context', { err });
    return false;
  }
}
